import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, CheckCircle2, XCircle, Clock, Server, FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

function StatCard({ label, value, icon: Icon, tone = "emerald", testid }) {
  const tones = {
    emerald: "text-emerald-brand bg-emerald-brand/12",
    sun: "text-yellow-700 bg-sun/25",
    crimson: "text-crimson bg-crimson/12",
    slate: "text-muted-foreground bg-muted",
  };
  return (
    <div className="rounded-2xl border border-border bg-white p-4 flex items-start justify-between" data-testid={testid}>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-display text-3xl font-semibold mt-1">{value ?? "—"}</div>
      </div>
      <div className={`h-9 w-9 rounded-xl grid place-items-center ${tones[tone]}`}>
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
    </div>
  );
}

export default function BulkDashboard() {
  const { activeCompanyId } = useAuth();
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState([]);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [{ data: s }, { data: cs }] = await Promise.all([
      api.get("/bulk/stats", { params }),
      api.get("/bulk/campaigns", { params }),
    ]);
    setStats(s);
    setRunning(cs.filter((c) => ["queued", "running", "scheduled", "paused"].includes(c.status)));
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [activeCompanyId]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="bulk-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total campaigns" value={stats?.total_campaigns} icon={Send} tone="emerald" testid="stat-total-campaigns" />
        <StatCard label="Running / queued" value={stats?.running} icon={Clock} tone="sun" testid="stat-running" />
        <StatCard label="Scheduled" value={stats?.scheduled} icon={Clock} tone="slate" testid="stat-scheduled" />
        <StatCard label="Delivered" value={stats?.deliveries?.sent} icon={CheckCircle2} tone="emerald" testid="stat-delivered" />
        <StatCard label="Failed" value={stats?.deliveries?.failed} icon={XCircle} tone="crimson" testid="stat-failed" />
        <StatCard label="SMTP profiles" value={stats?.profiles} icon={Server} tone="slate" testid="stat-profiles" />
        <StatCard label="Templates" value={stats?.templates} icon={FileText} tone="slate" testid="stat-templates" />
        <div className="rounded-2xl border border-dashed border-emerald-brand/40 bg-emerald-brand/5 p-4 flex flex-col justify-center">
          <Link to="/bulk/campaigns" className="text-emerald-brand text-sm font-medium">
            <Button className="w-full rounded-xl" data-testid="bulk-new-campaign-cta"><Plus className="h-4 w-4 mr-1.5" /> New campaign</Button>
          </Link>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white">
        <div className="p-4 border-b border-border">
          <h3 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">In-flight campaigns</h3>
        </div>
        {running.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-inflight">No running or scheduled campaigns.</div>
        ) : (
          <table className="tbl w-full">
            <thead><tr><th>Name</th><th>Status</th><th>Progress</th><th></th></tr></thead>
            <tbody>
              {running.map((c) => {
                const pct = c.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0;
                return (
                  <tr key={c.id} data-testid={`inflight-row-${c.id}`}>
                    <td className="font-medium">{c.name}</td>
                    <td><span className="text-[10px] font-mono uppercase text-emerald-brand">{c.status}</span></td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-brand to-sun" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-[10px] text-muted-foreground">{c.sent}/{c.total}</span>
                      </div>
                    </td>
                    <td className="text-right"><Link to={`/bulk/campaigns/${c.id}`} className="text-xs text-emerald-brand hover:underline">Open →</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
